import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  Tooltip,
  Card,
  Typography,
  Switch,
  message,
  Modal
} from "antd";
import { EyeOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import request from "helper/request";
import clientBillingApi from "../../billing/store/api";
import moment from "moment";

const { Title } = Typography;

const PurchaseOrdersPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [entitlements, setEntitlements] = useState([]);
  const [displayInventoryOnPOs, setDisplayInventoryOnPOs] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [fulfillingOrderId, setFulfillingOrderId] = useState(null);

  useEffect(() => {
    fetchPurchaseOrders(1, 10);
    fetchEntitlements();
  }, []);

  const fetchPurchaseOrders = async (page, limit) => {
    setLoading(true);
    try {
      const response = await request.get("/purchase-order", {
        params: { page, limit }
      });

      // Note: request interceptor returns response.data directly
      setPurchaseOrders(response.items || []);
      setPagination({
        current: page,
        pageSize: limit,
        total: response.meta?.totalItems || 0
      });
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntitlements = async () => {
    try {
      const response = await clientBillingApi.getClientEntitlements();
      setEntitlements(response || []);

      // Find Inventory Management entitlement and check for displayInventoryOnPOs setting
      const inventoryEnt = (response || []).find(
        (ent) => ent.name === "Inventory Management"
      );

      if (inventoryEnt && inventoryEnt.client_is_active) {
        const settings = inventoryEnt.settings || {};
        setDisplayInventoryOnPOs(
          settings.displayInventoryOnPOs === 1 || settings.displayInventoryOnPOs === true
        );
      }
    } catch (error) {
      console.error("Error fetching entitlements:", error);
    }
  };

  const handleInventoryToggle = async (checked) => {
    setToggleLoading(true);
    try {
      // Find Inventory Management entitlement
      const inventoryEnt = entitlements.find(
        (ent) => ent.name === "Inventory Management"
      );

      if (!inventoryEnt) {
        message.error("Inventory Management module not found");
        return;
      }

      // Update the entitlement with the new setting
      const currentSettings = inventoryEnt.settings || {};
      const updatedSettings = {
        ...currentSettings,
        displayInventoryOnPOs: checked ? 1 : 0
      };

      await clientBillingApi.updateClientEntitlement(
        inventoryEnt.id,
        inventoryEnt.client_is_active,
        updatedSettings
      );

      setDisplayInventoryOnPOs(checked);
      message.success(
        checked
          ? "Inventory display enabled for Purchase Orders"
          : "Inventory display disabled for Purchase Orders"
      );
    } catch (error) {
      console.error("Error updating entitlement:", error);
      message.error("Failed to update setting. Please try again.");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    fetchPurchaseOrders(paginationConfig.current, paginationConfig.pageSize);
  };

  const handleFulfill = async (purchaseOrder) => {
    setFulfillingOrderId(purchaseOrder.id);

    try {
      console.log("🔍 Purchase Order:", purchaseOrder);
      console.log("🔍 Products:", purchaseOrder.products);

      // Check if this PO has inventory items
      const hasInventoryItems = purchaseOrder.products.some((p) => p.inventoryItemId);

      console.log("🔍 Has inventory items?", hasInventoryItems);

      if (!hasInventoryItems) {
        // Generic PO without inventory - simple route creation
        Modal.info({
          title: "Generic Purchase Order",
          content:
            "This purchase order does not have inventory items. Creating a simple route...",
          onOk: () => {
            history.push("/routes/create", {
              purchaseOrder,
              type: "generic"
            });
          }
        });
        return;
      }

      // Fetch inventory items with warehouse info
      const inventoryItemIds = purchaseOrder.products
        .filter((p) => p.inventoryItemId)
        .map((p) => p.inventoryItemId);

      const inventoryResponse = await request.post(
        "/v1/inventory-management/items/batch",
        { itemIds: inventoryItemIds }
      );

      const inventoryItems = inventoryResponse.items || [];

      // Group by warehouse/savedLocation
      const warehouseGroups = {};

      purchaseOrder.products.forEach((product) => {
        if (!product.inventoryItemId) return;

        const inventoryItem = inventoryItems.find(
          (item) => item.id === product.inventoryItemId
        );

        if (!inventoryItem || !inventoryItem.warehouse) {
          return;
        }

        const warehouseId = inventoryItem.warehouse.id;
        if (!warehouseGroups[warehouseId]) {
          warehouseGroups[warehouseId] = {
            warehouse: inventoryItem.warehouse,
            savedLocation: inventoryItem.warehouse.savedLocation,
            products: []
          };
        }

        warehouseGroups[warehouseId].products.push({
          ...product,
          inventoryItem
        });
      });

      // Navigate to route creation with warehouse data
      history.push("/routes/create", {
        purchaseOrder,
        warehouseGroups,
        type: "inventory"
      });
    } catch (error) {
      console.error("Error fulfilling purchase order:", error);
      message.error("Failed to prepare fulfillment. Please try again.");
    } finally {
      setFulfillingOrderId(null);
    }
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "id",
      key: "id",
      render: (id) => id.substring(0, 8)
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName"
    },
    {
      title: "Vendor",
      dataIndex: "vendorCompanyName",
      key: "vendorCompanyName"
    },
    {
      title: "Vendor ID",
      dataIndex: "vendorId",
      key: "vendorId",
      render: (vendorId) => vendorId || "-"
    },
    {
      title: "Products",
      dataIndex: "products",
      key: "products",
      render: (products) => products?.length || 0
    },
    {
      title: "Status",
      dataIndex: "isFulfilled",
      key: "isFulfilled",
      render: (isFulfilled) =>
        isFulfilled ? (
          <Tag color="success">Fulfilled</Tag>
        ) : (
          <Tag color="processing">Pending</Tag>
        )
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("MMM DD, YYYY HH:mm")
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => console.log("View details:", record.id)}
            />
          </Tooltip>
          {!record.isFulfilled && (
            <Tooltip title="Create route from this purchase order">
              <Button
                type="link"
                icon={<CheckCircleOutlined />}
                onClick={() => handleFulfill(record)}
                loading={fulfillingOrderId === record.id}
              >
                Fulfill
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px"
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Purchase Orders
          </Title>
          <Space>
            <span>Display Inventory for Purchase Orders?</span>
            <Switch
              checked={displayInventoryOnPOs}
              onChange={handleInventoryToggle}
              loading={toggleLoading}
            />
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={purchaseOrders}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default PurchaseOrdersPage;
